import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db, firebaseConfig } from '../config/fbConfig';
import {
  collection,
  setDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { createUserWithEmailAndPassword, getAuth, deleteUser } from "firebase/auth";
import { initializeApp, getApp, getApps } from 'firebase/app';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

// Secondary Firebase App for creating users without affecting main auth
let secondaryApp;
let secondaryAuth;

if (!getApps().some(app => app.name === 'Secondary')) {
  secondaryApp = initializeApp(firebaseConfig, 'Secondary');
} else {
  secondaryApp = getApp('Secondary');
}
secondaryAuth = getAuth(secondaryApp);

Modal.setAppElement('#root');

const Users = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [ranks, setRanks] = useState([]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    email: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    taxId: '',
    department: '',
    rank: '',
    dateOfFirstEmployment: '',
    isActive: true,
    status: 'Staff'
  });

  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [isFetchingUsers, setIsFetchingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const usersCollectionRef = collection(db, "users");
  const departmentsCollectionRef = collection(db, "departments");
  const ranksCollectionRef = collection(db, "ranks");

  // Fetch departments and ranks
  useEffect(() => {
    const fetchDepartmentsAndRanks = async () => {
      try {
        const [deptSnap, rankSnap] = await Promise.all([
          getDocs(departmentsCollectionRef),
          getDocs(ranksCollectionRef)
        ]);

        const deptList = deptSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const rankList = rankSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setDepartments(deptList.sort((a, b) => a.name.localeCompare(b.name)));
        setRanks(rankList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error("Failed to load departments/ranks:", err);
        setError("Failed to load departments or ranks");
        setShowModal(true);
      }
    };

    fetchDepartmentsAndRanks();
    fetchUsers();
  }, []);

  // Fetch all users
  const fetchUsers = async () => {
    setIsFetchingUsers(true);
    try {
      const data = await getDocs(usersCollectionRef);
      const allUsers = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      const filtered = allUsers.filter(u => u.status !== 'Supreme');
      setUsers(filtered);
      setFilteredUsers(filtered);
    } catch (error) {
      setError("Failed to fetch staff");
      setShowModal(true);
    } finally {
      setIsFetchingUsers(false);
    }
  };

  // Search functionality
  useEffect(() => {
    const term = searchQuery.trim().toLowerCase();
    if (term) {
      const filtered = users.filter(u => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const phone = (u.phone || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const dept = (u.department || '').toLowerCase();
        const rank = (u.rank || '').toLowerCase();
        return name.includes(term) || phone.includes(term) || email.includes(term) || dept.includes(term) || rank.includes(term);
      });
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Add new staff
  const handleAddUser = async () => {
    setError(""); 
    setSuccessMessage("");

    const requiredFields = ["firstName", "lastName", "phone", "address", "email", "department", "status"];
    const missing = requiredFields.filter(field => !formData[field]?.trim());

    if (missing.length > 0) {
      setError("Please fill all required fields including Department");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, formData.email, '123456');
      const uid = cred.user.uid;

      await setDoc(doc(db, "users", uid), {
        uid,
        ...formData,
        department: formData.department,
        // rank: formData.rank || null,
        rank: formData.rank ? formData.rank : null,
        dateOfFirstEmployment: formData.dateOfFirstEmployment
          ? Timestamp.fromDate(new Date(formData.dateOfFirstEmployment))
          : null,
        isActive: true,
        createdAt: Timestamp.now(),
      });

      setSuccessMessage("Staff added successfully!");
      fetchUsers();
      setFormData({
        firstName: '', lastName: '', phone: '', address: '', email: '',
        bankName: '', accountName: '', accountNumber: '', taxId: '',
        department: '', rank: '', dateOfFirstEmployment: '',
        isActive: true, status: 'Staff'
      });
      setIsAddModalOpen(false);
    } catch (err) {
      setError(err.code === 'auth/email-already-in-use'
        ? "This email is already in use."
        : "Failed to add staff: " + err.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  // Update staff
  const handleUpdateUser = async () => {
    setUpdate(true);
    try {
      const userDoc = doc(db, "users", editingUser.id);
      await updateDoc(userDoc, {
        ...formData,
        department: formData.department,
        // rank: formData.rank || null,
        rank: formData.rank ? formData.rank : null,
        dateOfFirstEmployment: formData.dateOfFirstEmployment
          ? Timestamp.fromDate(new Date(formData.dateOfFirstEmployment))
          : null,
      });

      setSuccessMessage("Staff updated successfully!");
      fetchUsers();
      setIsEditModalOpen(false);
      setEditingUser(null);
    } catch (err) {
      setError("Failed to update staff: " + err.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  // Delete staff
  const handleDeleteUser = async () => {
    setUpdate(true);
    try {
      await deleteDoc(doc(db, "users", deletingUser.id));
      setSuccessMessage("Staff deleted successfully");
      fetchUsers();
      setIsDeleteModalOpen(false);
      setDeletingUser(null);
    } catch (err) {
      setError("Failed to delete staff");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleBulkImport = async () => {
    if (!file) {
      setImportStatus("Please select a file");
      return;
    }

    setImportStatus("Reading file...");
    setImportProgress(10);
    setError("");
    setSuccessMessage("");

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        let rows = [];
        const data = e.target.result;

        if (fileType === 'excel') {
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        } else {
          const result = Papa.parse(data, { header: true, skipEmptyLines: true });
          if (result.errors.length) throw new Error("CSV parsing error");
          rows = result.data;
        }

        if (rows.length === 0) {
          setImportStatus("No data found in file");
          return;
        }

        setImportStatus(`Processing ${rows.length} staff...`);
        setImportProgress(20);

        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        const requiredFields = ["firstName", "lastName", "phone", "address", "email", "department", "status"];

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const rowNum = i + 2; // Excel row number

          // Trim strings
          Object.keys(row).forEach(k => {
            if (typeof row[k] === 'string') row[k] = row[k].trim();
          });

          // Validate required fields
          const missing = requiredFields.filter(f => !row[f]);
          if (missing.length > 0) {
            errors.push(`Row ${rowNum}: Missing: ${missing.join(', ')}`);
            errorCount++;
            continue;
          }

          // Validate department exists
          const deptExists = departments.some(d => d.name.toLowerCase() === row.department.toLowerCase());
          if (!deptExists) {
            errors.push(`Row ${rowNum}: Department "${row.department}" not found`);
            errorCount++;
            continue;
          }

          // Validate status
          const validStatuses = ["Staff", "Cashier", "Front Desk", "Admin"];
          if (!validStatuses.includes(row.status)) {
            errors.push(`Row ${rowNum}: Invalid status "${row.status}"`);
            errorCount++;
            continue;
          }

          try {
            // Create Firebase Auth user
            const cred = await createUserWithEmailAndPassword(
              secondaryAuth,
              row.email.toLowerCase(),
              '123456'
            );
            const uid = cred.user.uid;

            // Save to Firestore
            await setDoc(doc(db, "users", uid), {
              uid,
              firstName: row.firstName,
              lastName: row.lastName,
              phone: row.phone,
              address: row.address,
              email: row.email.toLowerCase(),
              department: row.department,
              status: row.status,
              rank: row.rank || null,
              dateOfFirstEmployment: row.dateOfFirstEmployment
                ? Timestamp.fromDate(new Date(row.dateOfFirstEmployment))
                : null,
              taxId: row.taxId || '',
              bankName: row.bankName || '',
              accountName: row.accountName || '',
              accountNumber: row.accountNumber || '',
              isActive: true,
              createdAt: Timestamp.now(),
            });

            successCount++;
          } catch (err) {
            const msg = err.code === 'auth/email-already-in-use'
              ? 'Email already exists'
              : err.message;
            errors.push(`Row ${rowNum}: ${row.email} → ${msg}`);
            errorCount++;
          }

          // Update progress
          setImportProgress(20 + Math.round(((i + 1) / rows.length) * 75));
        }

        setImportProgress(100);
        const summary = `Done! ${successCount} added, ${errorCount} failed.`;
        setImportStatus(summary);
        setSuccessMessage(summary);

        if (errors.length > 0) {
          setError(`Failed rows:\n${errors.slice(0, 15).join('\n')}${errors.length > 15 ? `\n...and ${errors.length - 15} more` : ''}`);
          setShowModal(true);
        }

        fetchUsers();

        // Auto-close on full success
        if (errorCount === 0) {
          setTimeout(() => {
            setIsBulkImportModalOpen(false);
            setFile(null);
            setImportProgress(0);
            setImportStatus('');
          }, 2500);
        }

      } catch (err) {
        setError("Import failed: " + err.message);
        setImportStatus("Import failed");
        setShowModal(true);
      }
    };

    reader.onerror = () => setImportStatus("Failed to read file");

    if (fileType === 'excel') {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  // Open edit modal
  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      ...user,
      dateOfFirstEmployment: user.dateOfFirstEmployment
        ? user.dateOfFirstEmployment.toDate().toISOString().split('T')[0]
        : ''
    });
    setIsEditModalOpen(true);
  };

  // Group users by department
  const groupedByDepartment = () => {
    const grouped = {};
    filteredUsers.forEach(u => {
      const dept = u.department || "Unassigned";
      if (!grouped[dept]) grouped[dept] = [];
      grouped[dept].push(u);
    });
    return grouped;
  };

  const grouped = groupedByDepartment();

  if (loading || !userData) return <Loader text={loading ? "LOADING" : "READING"} />;

  const allowedRoles = ['Admin', 'Supreme'];

  return (
    <>
      {update && <Loader text="PROCESSING" />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Manage Staff</h2>
            <Link to="#" className="home-link" onClick={(e) => { e.preventDefault(); fetchUsers(); }}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons">
              <button className="login-button login-button-d caps" onClick={() => {
                setFormData({
                  firstName: '',
                  lastName: '',
                  phone: '',
                  address: '',
                  email: '',
                  bankName: '',
                  accountName: '',
                  accountNumber: '',
                  taxId: '',
                  department: '',
                  rank: '',
                  dateOfFirstEmployment: '',
                  isActive: true,
                  status: 'Staff'
                });
                setIsAddModalOpen(true)}}>
                Add Staff
              </button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>
                Bulk Import
              </button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name, phone, email, department or rank"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed2">
              <div className="class-list class-list-d scrollable">
                {isFetchingUsers ? (
                  <p className="male-female-stat">Loading staff...</p>
                ) : Object.keys(grouped).length === 0 ? (
                  <p className="male-female-stat">No staff found. Please add staff with departments.</p>
                ) : (
                  Object.keys(grouped).sort().map(deptName => (
                    <div key={deptName} className="mb-30">
                      <h3 className="size5 caps">
                        {deptName}
                        <span className="">&nbsp;({grouped[deptName].length} staff)</span>
                      </h3>
                      {grouped[deptName].map(u => (
                        <div key={u.id} className="class-item">
                          <p style={{wordBreak: 'break-word'}}>
                            <strong className="size5">{u.firstName} {u.lastName}</strong><br />
                            {u.rank && <><small>{ranks.find(r => r.id === u.rank)?.name || 'Unknown Rank'}</small><br /></>}
                            <span className="size5 font-gold ml-10">({u.status})</span>
                            {!u.isActive && <span className="size5 font-red ml-10">(Inactive)</span>}
                            <br />
                            <small>{u.phone}</small><br />
                            <small>{u.email}</small><br />                            
                          </p>
                          <div className="row align-center justify-center gap-15">
                            <button className="list-btn" onClick={() => { setViewingUser(u); setIsViewModalOpen(true); }}>
                              <i className="fas fa-eye"></i>
                            </button>
                            <button className="list-btn" onClick={() => openEdit(u)}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="list-btn delete-btn" onClick={() => { setDeletingUser(u); setIsDeleteModalOpen(true); }}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {/* Success/Error Modal */}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Staff Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Staff</h2>
        <form className="product-form modal-scroll">
          <div className="form-row">
            <div className="form-group">
              <label className="input-label">First Name <span className="required">*</span></label>
              <input name="firstName" className="input-field" value={formData.firstName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Last Name <span className="required">*</span></label>
              <input name="lastName" className="input-field" value={formData.lastName} onChange={handleInputChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Phone <span className="required">*</span></label>
              <input name="phone" type="tel" className="input-field" value={formData.phone} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Email <span className="required">*</span></label>
              <input name="email" type="email" className="input-field" value={formData.email} onChange={handleInputChange} />
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">Address <span className="required">*</span></label>
            <input name="address" className="input-field" value={formData.address} onChange={handleInputChange} />
          </div>

          <div className="form-group">
            <label className="input-label">Department <span className="required">*</span></label>
            <select name="department" className="input-field" value={formData.department} onChange={handleInputChange}>
              <option value="">Select Department</option>
              {departments.map(d => (
                <option key={d.id} value={d.name}>{d.name} ({d.code || 'No Code'})</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Rank</label>              
              <select name="rank" className="input-field" value={formData.rank} onChange={handleInputChange}>
                <option value="">Select Rank</option>
                {ranks.map(r => (
                  <option key={r.id} value={r.id}>{r.name} - {r.gradeLevel} (₦{parseFloat(r.basicSalary).toLocaleString()})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="input-label">Role <span className="required">*</span></label>
              <select name="status" className="input-field" value={formData.status} onChange={handleInputChange}>
                <option value="Staff">Staff</option>
                <option value="Front Desk">Front Desk</option>
                <option value="Cashier">Cashier</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">Date of First Employment</label>
            <input name="dateOfFirstEmployment" type="date" className="input-field" value={formData.dateOfFirstEmployment} onChange={handleInputChange} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Tax ID</label>
              <input name="taxId" className="input-field" value={formData.taxId} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Bank Name</label>
              <input name="bankName" className="input-field" value={formData.bankName} onChange={handleInputChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Account Name</label>
              <input name="accountName" className="input-field" value={formData.accountName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Account Number</label>
              <input name="accountNumber" className="input-field" value={formData.accountNumber} onChange={handleInputChange} />
            </div>
          </div>

          <div className="row justify-center gap-15 mt-20">
            <button type="button" className="login-button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button type="button" className="login-button" onClick={handleAddUser}>Add Staff</button>
          </div>
        </form>
      </Modal>

      {/* Edit Staff Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); setEditingUser(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Staff</h2>
        <form className="product-form modal-scroll">
          <div className="form-row">
            <div className="form-group">
              <label className="input-label">First Name <span className="required">*</span></label>
              <input name="firstName" className="input-field" value={formData.firstName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Last Name <span className="required">*</span></label>
              <input name="lastName" className="input-field" value={formData.lastName} onChange={handleInputChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Phone <span className="required">*</span></label>
              <input name="phone" type="tel" className="input-field" value={formData.phone} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Email <span className="required">*</span></label>
              <input name="email" type="email" className="input-field" value={formData.email} onChange={handleInputChange} disabled />
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">Address <span className="required">*</span></label>
            <input name="address" className="input-field" value={formData.address} onChange={handleInputChange} />
          </div>

          <div className="form-group">
            <label className="input-label">Department <span className="required">*</span></label>
            <select name="department" className="input-field" value={formData.department} onChange={handleInputChange}>
              <option value="">Select Department</option>
              {departments.map(d => (
                <option key={d.id} value={d.name}>{d.name} ({d.code || 'No Code'})</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Rank</label>
              <select name="rank" className="input-field" value={formData.rank} onChange={handleInputChange}>
                <option value="">Select Rank</option>
                {ranks.map(r => (
                  <option key={r.id} value={r.id}>{r.name} - {r.gradeLevel} (₦{parseFloat(r.basicSalary).toLocaleString()})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="input-label">Role <span className="required">*</span></label>
              <select name="status" className="input-field" value={formData.status} onChange={handleInputChange}>
                <option value="Staff">Staff</option>
                <option value="Cashier">Cashier</option>
                <option value="Front Desk">Front Desk</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">Date of First Employment</label>
            <input name="dateOfFirstEmployment" type="date" className="input-field" value={formData.dateOfFirstEmployment} onChange={handleInputChange} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Tax ID</label>
              <input name="taxId" className="input-field" value={formData.taxId} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Bank Name</label>
              <input name="bankName" className="input-field" value={formData.bankName} onChange={handleInputChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="input-label">Account Name</label>
              <input name="accountName" className="input-field" value={formData.accountName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label className="input-label">Account Number</label>
              <input name="accountNumber" className="input-field" value={formData.accountNumber} onChange={handleInputChange} />
            </div>
          </div>

          <div className="form-group">
            <label>
              <input type="checkbox" className="gold-tick" checked={formData.isActive} onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))} />
              Active Staff
            </label>
          </div>

          <div className="row justify-center gap-15 mt-20">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateUser}>Update Staff</button>
          </div>
        </form>
      </Modal>

      {/* View Staff Modal */}
      <Modal isOpen={isViewModalOpen} onRequestClose={() => setIsViewModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Staff Details</h2>
        {viewingUser && (
          <div className="column gap-12 modal-scroll">
            <p><strong>Name:</strong> {viewingUser.firstName} {viewingUser.lastName}</p>
            <p><strong>Department:</strong> {viewingUser.department || 'Unassigned'}</p>
            <p><strong>Role:</strong> {viewingUser.status}</p>
            <p><strong>Rank:</strong> {viewingUser.rank ? (ranks.find(r => r.id === viewingUser.rank)?.name || 'Unknown Rank') : 'Not set'}</p>
            <p><strong>Date of First Employment:</strong> {viewingUser.dateOfFirstEmployment
              ? new Date(viewingUser.dateOfFirstEmployment.toDate()).toLocaleDateString('en-GB')
              : 'Not set'}</p>
            <p><strong>Phone:</strong> {viewingUser.phone}</p>
            <p><strong>Email:</strong> {viewingUser.email}</p>
            <p><strong>Address:</strong> {viewingUser.address}</p>
            <p><strong>Tax ID:</strong> {viewingUser.taxId || 'N/A'}</p>
            <p><strong>Bank:</strong> {viewingUser.bankName || 'N/A'}</p>
            <p><strong>Account Name:</strong> {viewingUser.accountName || 'N/A'}</p>
            <p><strong>Account Number:</strong> {viewingUser.accountNumber || 'N/A'}</p>
            <p><strong>Status:</strong> <span className={viewingUser.isActive ? "font-green" : "font-red"}>{viewingUser.isActive ? 'Active' : 'Inactive'}</span></p>
            <button className="login-button caps" onClick={() => setIsViewModalOpen(false)}>Close</button>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onRequestClose={() => setIsDeleteModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Delete Staff</h2>
        <p>Are you sure you want to delete <strong>{deletingUser?.firstName} {deletingUser?.lastName}</strong>?</p>
        <p>This action cannot be undone.</p>
        <div className="row justify-center gap-15">
          <button className="login-button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
          <button className="login-button delete-btn" onClick={handleDeleteUser}>Delete</button>
        </div>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={isBulkImportModalOpen} onRequestClose={() => { setIsBulkImportModalOpen(false); setFile(null); setImportProgress(0); setImportStatus(''); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Bulk Import Staff</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '10px' }}>
            <label><input type="radio" name="fileType" value="excel" checked={fileType === 'excel'} onChange={(e) => setFileType(e.target.value)} /> Excel (.xlsx)</label>{' '}
            <label><input type="radio" name="fileType" value="csv" checked={fileType === 'csv'} onChange={(e) => setFileType(e.target.value)} /> CSV</label>
          </div>
          <input type="file" accept={fileType === 'excel' ? '.xlsx,.xls' : '.csv'} onChange={(e) => setFile(e.target.files[0])} className="input-field1" />
          {importStatus && <p>{importStatus} {importProgress > 0 && `( ${importProgress}% )`}</p>}
          <div className="row justify-center gap-15 mt-20">
            <button className="login-button caps" onClick={() => { setIsBulkImportModalOpen(false); setFile(null); }}>Cancel</button>
            <button 
              className="login-button caps" 
              onClick={handleBulkImport}
              disabled={!file || (importProgress > 0 && importProgress < 100)}
            >
              {importProgress > 0 && importProgress < 100 
                ? `Importing... ${importProgress}%` 
                : "Import Staff"
              }
            </button>
          </div>
          <div className="import-instructions">
            <h4>Required Columns:</h4>
            <p>firstName, lastName, phone, address, email, department, status</p>
            <h4>Optional Columns:</h4>
            <p>rank, dateOfFirstEmployment (YYYY-MM-DD), taxId, bankName, accountName, accountNumber</p>
            <p>Default password: <strong>123456</strong></p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Users;
